import React, { useState, useEffect, useRef } from "react";
import { FaWhatsapp } from "react-icons/fa";
import "../design/WhatsAppIcon.css"; // Your CSS file
import logo from "../../assets/logo/Franmaxindia logo@4x.png";

// --- Conversation Map ---
const conversationMap = {
  START: {
    bot: [
      "Hi there 👋! I'm the virtual assistant for Franmax India. Are you looking to invest in a franchise or just exploring options?",
    ],
    suggestions: [
      { text: "Explore Franchise Options", next: "EXPLORE_OPTIONS" },
      { text: "What's your investment range?", next: "INVEST_BUDGET" },
      { text: "About Franmax India", next: "ABOUT_US" },
      { text: "Talk to a consultant", next: "TALK_TO_CONSULTANT" },
    ],
  },
  TALK_TO_CONSULTANT: {
    bot: [
      <>
        Absolutely! Our consultants are ready to help. You can reach us on WhatsApp or give us a direct call for personalized guidance.
        <div className="action-buttons-container">
          <button className="action-btn whatsapp" onClick={() => window.open("https://wa.me/919403890794", "_blank")}>
            <FaWhatsapp /> Chat on WhatsApp
          </button>
          <button className="action-btn call" onClick={() => window.open("tel:+918140038080")}>
            📞 Call Us
          </button>
        </div>
      </>
    ],
    suggestions: [
      { text: "Back to Main Menu", next: "START" },
    ],
  },
  // ... (rest of your conversationMap)
  EXPLORE_OPTIONS: { bot: ["Excellent choice! We categorize our franchises by industry. Which sector are you most interested in exploring?"], suggestions: [{ text: "🍔 Food & Beverage", next: "SECTOR_FNB" }, { text: "🛍️ Retail", next: "SECTOR_RETAIL" }, { text: "🎓 Education", next: "SECTOR_EDUCATION" }, { text: "Back to Main Menu", next: "START" }] },
  INVEST_BUDGET: { bot: ["Our portfolio includes franchises with investments from under ₹10 Lakh to over ₹50 Lakh. Knowing your approximate budget will help me suggest the best-fit options."], suggestions: [{ text: "Below ₹10 Lakh", next: "BUDGET_LOW" }, { text: "₹10L - ₹25L", next: "BUDGET_MEDIUM" }, { text: "₹25L - ₹50L", next: "BUDGET_HIGH" }] },
  ABOUT_US: { bot: ["Franmax India is a premier franchise consulting firm. We exist to simplify your journey into business ownership, from finding the right brand to launching it successfully."], suggestions: [{ text: "See Franchise Options", next: "EXPLORE_OPTIONS" }, { text: "Discuss my budget", next: "INVEST_BUDGET" }] },
  SECTOR_FNB: { bot: ["The Food & Beverage sector is booming! To narrow it down, what's your investment level?"], suggestions: [{ text: "< ₹15 Lakh", next: "BUDGET_LOW" }, { text: "₹15L - ₹30L", next: "BUDGET_MEDIUM" }, { text: "> ₹30L", next: "BUDGET_HIGH" }] },
  BUDGET_LOW: { bot: ["In this range, we have excellent opportunities like express food kiosks and small retail setups with quick ROI."], suggestions: [{ text: "Talk to a consultant", next: "TALK_TO_CONSULTANT" }, { text: "See other sectors", next: "EXPLORE_OPTIONS" }] },
  BUDGET_MEDIUM: { bot: ["This is a great budget for popular cafe franchises or mid-sized retail stores."], suggestions: [{ text: "Talk to a consultant", next: "TALK_TO_CONSULTANT" }, { text: "See other sectors", next: "EXPLORE_OPTIONS" }] },
  BUDGET_HIGH: { bot: ["With this investment, you can look at premium casual dining restaurants or established school franchises."], suggestions: [{ text: "Talk to a consultant", next: "TALK_TO_CONSULTANT" }] },
  GREETINGS: { bot: ["Hi there! 👋 Great to see you again."], suggestions: [{ text: "Explore Franchise Options", next: "EXPLORE_OPTIONS" }, { text: "Talk to a consultant", next: "TALK_TO_CONSULTANT" }] },
  FAREWELL: { bot: ["Goodbye! 👋 Come back anytime."], suggestions: [{ text: "Start Over", next: "START" }] },
  SMALL_TALK: { bot: ["I'm doing great! How's your day going? 😊"], suggestions: [{ text: "Tell me a joke", next: "JOKE" }, { text: "Back to main menu", next: "START" }] },
  JOKE: { bot: ["Why did the entrepreneur go broke? Because he couldn't find the right franchise! 😅"], suggestions: [{ text: "Another joke", next: "JOKE" }, { text: "Back to main menu", next: "START" }] },
  FALLBACK: { bot: ["My apologies, I'm not equipped to handle that specific query. Would you like me to show you our main menu?"], suggestions: [{ text: "Explore Franchise Options", next: "EXPLORE_OPTIONS" }, { text: "Talk to a consultant", next: "TALK_TO_CONSULTANT" }] },
};

// --- Utility Functions ---
const getRandomBotMessage = (key) => {
  const messages = conversationMap[key]?.bot;
  if (!messages || messages.length === 0) return "I'm not sure how to respond to that.";
  return messages[Math.floor(Math.random() * messages.length)];
};

// --- Helper: Detect Mobile Device ---
const isMobileDevice = () => {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) 
    || window.innerWidth <= 768;
};

// --- React Component ---
const WhatsAppChat = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [typing, setTyping] = useState(false);
  const [currentNode, setCurrentNode] = useState("START");
  const [visibleSuggestions, setVisibleSuggestions] = useState([]);
  const chatBodyRef = useRef(null);

  const toggleChat = () => setIsOpen(!isOpen);

  useEffect(() => {
    // Only auto-open on desktop (non-mobile) devices after 3 seconds
    if (!isMobileDevice()) {
      const timer = setTimeout(() => setIsOpen(true), 3000);
      return () => clearTimeout(timer);
    }
    // For mobile, don't auto-open - just leave it closed
  }, []);

  useEffect(() => {
    if (chatBodyRef.current) chatBodyRef.current.scrollTop = chatBodyRef.current.scrollHeight;
  }, [messages, typing, visibleSuggestions]);

  useEffect(() => {
    // Only show initial bot message when chat is opened manually and no messages exist
    if (!isOpen || messages.length > 0) return;
    setTyping(true);
    setTimeout(() => {
      setTyping(false);
      addBotMessage("START");
    }, 1200);
  }, [isOpen]);

  const addBotMessage = (nodeKey) => {
    const botMsg = {
      id: Date.now(),
      type: "incoming",
      text: getRandomBotMessage(nodeKey),
      time: new Date(),
    };
    setMessages((prev) => [...prev, botMsg]);
    setCurrentNode(nodeKey);
    showSuggestions(conversationMap[nodeKey]?.suggestions || []);
  };

  const showSuggestions = (suggestions) => {
    setVisibleSuggestions([]);
    suggestions.forEach((s, i) => {
      setTimeout(() => setVisibleSuggestions((prev) => [...prev, s.text]), i * 300);
    });
  };

  const handleUserReply = (text) => {
    setMessages((prev) => [...prev, { id: Date.now(), type: "outgoing", text, time: new Date() }]);
    setVisibleSuggestions([]);

    setTyping(true);
    setTimeout(() => {
      setTyping(false);

      const lower = text.toLowerCase();
      if (/\b(hi|hello|hey|good morning|good evening)\b/.test(lower)) {
        addBotMessage("GREETINGS");
      } else if (/\b(bye|goodbye|see you)\b/.test(lower)) {
        addBotMessage("FAREWELL");
      } else if (/\b(how are you|how's your day|what's up)\b/.test(lower)) {
        addBotMessage("SMALL_TALK");
      } else if (/\b(joke|funny|make me laugh)\b/.test(lower)) {
        addBotMessage("JOKE");
      } else {
        const nextNode = conversationMap[currentNode]?.suggestions.find((s) => s.text === text)?.next || "FALLBACK";
        addBotMessage(nextNode);
      }
    }, 1200 + Math.random() * 1500);
  };

  const getTimeString = (date) => {
    const hours = date.getHours() % 12 || 12;
    const minutes = date.getMinutes().toString().padStart(2, "0");
    const ampm = date.getHours() >= 12 ? "PM" : "AM";
    return `${hours}:${minutes} ${ampm}`;
  };

  return (
    <div className="chat-btn">
      {isOpen && (
        <div className="chat-window">
          <div className="chat-header">
            <div className="chat-avatar"><img src={logo} alt="Franmax India" /></div>
            <div className="chat-info">
              <div className="chat-name">Franmax India</div>
              <div className="chat-status"><span className="online-dot"></span> online</div>
            </div>
            <div onClick={toggleChat} className="chat-close-btn">✕</div>
          </div>

          <div className="chat-body" ref={chatBodyRef}>
            {messages.map((msg) => (
              <div key={msg.id} className={`chat-message ${msg.type}`}>
                <div className={`chat-bubble ${msg.type === "incoming" ? "incoming-bubble" : "outgoing-bubble"}`}>
                  {msg.text}
                </div>
                <div className="chat-time">{getTimeString(msg.time)}</div>
              </div>
            ))}
            {typing && (
              <div className="chat-message incoming">
                <div className="chat-bubble incoming-bubble typing-indicator"><span></span><span></span><span></span></div>
              </div>
            )}
          </div>

          <div className="chat-footer">
            <div className="chat-input-container">
              <input
                type="text"
                placeholder="Type your message..."
                className="chat-input"
                onKeyDown={(e) => {
                  if (e.key === "Enter" && e.target.value.trim() !== "") {
                    handleUserReply(e.target.value);
                    e.target.value = "";
                  }
                }}
              />
              <button className="chat-btn-start" onClick={() => window.open("https://wa.me/919403890794", "_blank")}><FaWhatsapp style={{ marginRight: "6px" }} /> WhatsApp</button>
            </div>

            <div className="suggestions-container">
              {visibleSuggestions.map((reply, i) => (
                <div key={i} className="suggestion-bubble" onClick={() => handleUserReply(reply)}>
                  {reply}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {!isOpen && (
        <div className="chat-btn-icon" onClick={toggleChat}><FaWhatsapp className="chat-icon" /></div>
      )}
    </div>
  );
};

export default WhatsAppChat;